import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { createEvent, changeLifecycle, EVENT_CATEGORIES, RADIUS_PRESETS, geocodeSuggest } from '@/lib/events-api';
import { ArrowLeft, Search, AlertTriangle, Info } from 'lucide-react';

function isoOrNull(v) {
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export default function CreateEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useRequireAuth();

  const [name, setName] = useState('');
  const [category, setCategory] = useState('wedding');
  const [startAt, setStartAt] = useState('2026-06-15T18:00');
  const [endAt, setEndAt] = useState('2026-06-15T23:00');
  const [tz, setTz] = useState('America/Chicago');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [venueLat, setVenueLat] = useState('');
  const [venueLng, setVenueLng] = useState('');
  const [radius, setRadius] = useState(500);
  const [customRadius, setCustomRadius] = useState('');
  const [uploadStart, setUploadStart] = useState('2026-06-15T15:00');
  const [uploadEnd, setUploadEnd] = useState('2026-06-16T12:00');
  const [videoSecs, setVideoSecs] = useState('120');
  const [fileMb, setFileMb] = useState('200');
  const [perContribCap, setPerContribCap] = useState('300');
  const [allowVideo, setAllowVideo] = useState(true);
  const [lookbookPrivate, setLookbookPrivate] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Address autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const suppressNextFetch = useRef(false);
  const lastFetchedQuery = useRef('');

  useEffect(() => {
    if (suppressNextFetch.current) { suppressNextFetch.current = false; lastFetchedQuery.current = venueAddress; return; }
    const q = venueAddress.trim();
    if (q.length < 3) { setSuggestions([]); setSuggestLoading(false); setSuggestError(null); setShowSuggest(false); return; }
    if (q === lastFetchedQuery.current) return;
    const handle = setTimeout(async () => {
      try {
        setSuggestLoading(true); setSuggestError(null);
        const res = await geocodeSuggest(q, 8);
        lastFetchedQuery.current = q;
        setSuggestions(res.suggestions || []);
        setShowSuggest(true);
      } catch (e) {
        setSuggestions([]); setSuggestError(e.message || "Couldn't reach address lookup."); setShowSuggest(true);
      } finally { setSuggestLoading(false); }
    }, 300);
    return () => clearTimeout(handle);
  }, [venueAddress]);

  function pickSuggestion(s) {
    suppressNextFetch.current = true;
    const fullAddr = s.address && s.address !== s.label ? `${s.label}, ${s.address}` : s.label;
    setVenueAddress(fullAddr);
    setVenueLat(s.lat.toFixed(6));
    setVenueLng(s.lng.toFixed(6));
    if (!venueName.trim() && s.label) setVenueName(s.label);
    setShowSuggest(false); setSuggestions([]);
  }

  async function onSubmit(e) {
    e.preventDefault(); setError(null);
    if (!name.trim()) return setError('Give your event a name.');
    if (!venueName.trim() || !venueAddress.trim()) return setError('Add a venue name and address.');
    const lat = parseFloat(venueLat), lng = parseFloat(venueLng);
    if (isNaN(lat) || isNaN(lng)) return setError('Venue latitude and longitude are required (numeric).');
    const start = isoOrNull(startAt), end = isoOrNull(endAt), uStart = isoOrNull(uploadStart), uEnd = isoOrNull(uploadEnd);
    if (!start || !end || !uStart || !uEnd) return setError('Please enter valid event and upload window times.');
    const finalRadius = customRadius ? parseInt(customRadius, 10) : radius;
    if (!finalRadius || finalRadius < 25) return setError('Radius must be at least 25 meters.');
    setSubmitting(true);
    try {
      const body = await createEvent({
        name: name.trim(), category, start_at: start, end_at: end, timezone: tz.trim() || 'UTC',
        venue_name: venueName.trim(), venue_address: venueAddress.trim(), venue_lat: lat, venue_lng: lng,
        radius_meters: finalRadius, upload_window_start: uStart, upload_window_end: uEnd,
        media_rules: { allow_photos: true, allow_videos: allowVideo, max_video_seconds: parseInt(videoSecs, 10) || 120, max_file_bytes: (parseInt(fileMb, 10) || 100) * 1024 * 1024, per_contributor_upload_cap: parseInt(perContribCap, 10) || 200 },
        lookbook_default_private: lookbookPrivate,
      });
      try { await changeLifecycle(body.event.id, 'SCHEDULED'); await changeLifecycle(body.event.id, 'COLLECTING'); } catch {}
      navigate(`/event-created?event_id=${encodeURIComponent(body.event.id)}`, { replace: true });
    } catch (err) {
      setError(err.message || 'Could not create event.');
    } finally { setSubmitting(false); }
  }

  const Field = ({ label, id, testId, ...props }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-[#0B1B46] mb-1.5">{label}</label>
      <input id={id} className="w-full h-11 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid={testId} {...props} />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="create-event-screen">
      <button onClick={() => navigate('/host-dashboard', { replace: true })} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="create-back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="create-title">Create Event</h1>
      <p className="text-sm text-[#5B6472] mb-6">Configure the essentials. You'll get an Event ID, QR code, and invite link on the next screen.</p>

      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        {/* Basics */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-4" data-testid="create-basics">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Basics</p>
          <Field label="Event name" id="create-name" testId="create-name" placeholder="Sarah & James Wedding" value={name} onChange={e => setName(e.target.value)} />
          <div>
            <p className="text-sm font-semibold text-[#0B1B46] mb-1.5">Category</p>
            <div className="flex flex-wrap gap-2" data-testid="create-category-chips">
              {EVENT_CATEGORIES.map(c => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${category === c.key ? 'bg-[#EFF5FF] border-[#146BFF] text-[#146BFF]' : 'bg-[#F3F5F8] border-[#E3E8EF] text-[#5B6472] hover:bg-[#E3E8EF]'}`}
                  data-testid={`create-category-${c.key}`}>{c.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-4" data-testid="create-schedule">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Schedule</p>
          <Field label="Start (YYYY-MM-DDTHH:mm)" id="create-start" testId="create-start-at" value={startAt} onChange={e => setStartAt(e.target.value)} />
          <Field label="End (YYYY-MM-DDTHH:mm)" id="create-end" testId="create-end-at" value={endAt} onChange={e => setEndAt(e.target.value)} />
          <Field label="Timezone (IANA)" id="create-tz" testId="create-timezone" value={tz} onChange={e => setTz(e.target.value)} />
        </div>

        {/* Venue */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-4" data-testid="create-venue">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Venue & Verification</p>
          <Field label="Venue name" id="create-venue-name" testId="create-venue-name" value={venueName} onChange={e => setVenueName(e.target.value)} />
          <div>
            <label htmlFor="create-venue-address" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Venue address</label>
            <input id="create-venue-address" value={venueAddress} onChange={e => setVenueAddress(e.target.value)}
              className="w-full h-11 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white" data-testid="create-venue-address" />
          </div>
          {showSuggest && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl overflow-hidden" data-testid="create-venue-suggest-panel">
              {suggestLoading ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-[#5B6472]" data-testid="create-venue-suggest-loading"><Search className="w-3.5 h-3.5 animate-spin" /> Searching addresses…</div>
              ) : suggestError ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-[#B86500]" data-testid="create-venue-suggest-error"><AlertTriangle className="w-3.5 h-3.5" /> {suggestError}</div>
              ) : suggestions.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-[#5B6472]" data-testid="create-venue-suggest-empty"><Search className="w-3.5 h-3.5" /> No matches yet — keep typing.</div>
              ) : (
                <>
                  {suggestions.map(s => (
                    <button key={s.id} type="button" onClick={() => pickSuggestion(s)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-[#E3E8EF] last:border-0 hover:bg-[#EFF5FF] transition-colors"
                      data-testid={`create-venue-suggest-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0B1B46] truncate">{s.label}</p>
                        {s.address && <p className="text-xs text-[#5B6472] truncate">{s.address}</p>}
                      </div>
                      {s.category && <span className="px-2 py-0.5 text-[10px] font-bold text-[#146BFF] bg-[#EFF5FF] rounded uppercase">{s.category}</span>}
                    </button>
                  ))}
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#F3F5F8] text-[11px] text-[#5B6472]">
                    <Info className="w-3 h-3" /> Address lookup proxied server-side · picking one fills coordinates automatically
                  </div>
                </>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude" id="create-lat" testId="create-venue-lat" placeholder="30.267153" value={venueLat} onChange={e => setVenueLat(e.target.value)} />
            <Field label="Longitude" id="create-lng" testId="create-venue-lng" placeholder="-97.743057" value={venueLng} onChange={e => setVenueLng(e.target.value)} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0B1B46] mb-1.5">Upload radius</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {RADIUS_PRESETS.map(r => (
                <button key={r} type="button" onClick={() => { setRadius(r); setCustomRadius(''); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${radius === r && !customRadius ? 'bg-[#EFF5FF] border-[#146BFF] text-[#146BFF]' : 'bg-[#F3F5F8] border-[#E3E8EF] text-[#5B6472]'}`}
                  data-testid={`create-radius-${r}`}>{r} m</button>
              ))}
            </div>
            <Field label="Custom radius (meters, optional)" id="create-radius-custom" testId="create-radius-custom" value={customRadius} onChange={e => setCustomRadius(e.target.value)} />
          </div>
        </div>

        {/* Upload window */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-4" data-testid="create-upload-window">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Upload Window</p>
          <Field label="Opens at (YYYY-MM-DDTHH:mm)" id="create-window-open" testId="create-window-open" value={uploadStart} onChange={e => setUploadStart(e.target.value)} />
          <Field label="Closes at (YYYY-MM-DDTHH:mm)" id="create-window-close" testId="create-window-close" value={uploadEnd} onChange={e => setUploadEnd(e.target.value)} />
        </div>

        {/* Media rules */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 flex flex-col gap-4" data-testid="create-media-rules">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Media Rules</p>
          <button type="button" onClick={() => setAllowVideo(v => !v)} className="flex items-center gap-3 py-2" data-testid="create-allow-video">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${allowVideo ? 'bg-[#146BFF] border-[#146BFF]' : 'border-[#E3E8EF]'}`}>
              {allowVideo && <span className="text-white text-xs font-bold">✓</span>}
            </div>
            <span className="text-sm text-[#0B1B46]">Allow video uploads</span>
          </button>
          <Field label="Max video seconds" id="create-video-secs" testId="create-video-secs" value={videoSecs} onChange={e => setVideoSecs(e.target.value)} />
          <Field label="Max file size (MB)" id="create-file-mb" testId="create-file-mb" value={fileMb} onChange={e => setFileMb(e.target.value)} />
          <Field label="Per-contributor upload cap" id="create-per-contrib" testId="create-per-contrib" value={perContribCap} onChange={e => setPerContribCap(e.target.value)} />
        </div>

        {/* Lookbook */}
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="create-lookbook">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Lookbook</p>
          <button type="button" onClick={() => setLookbookPrivate(v => !v)} className="flex items-center gap-3 py-2" data-testid="create-lookbook-private">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${lookbookPrivate ? 'bg-[#146BFF] border-[#146BFF]' : 'border-[#E3E8EF]'}`}>
              {lookbookPrivate && <span className="text-white text-xs font-bold">✓</span>}
            </div>
            <span className="text-sm text-[#0B1B46]">Keep Lookbook private by default (recommended)</span>
          </button>
        </div>

        {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="create-error">{error}</div>}

        <button type="submit" disabled={submitting}
          className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
          data-testid="create-submit-btn">{submitting ? 'Creating…' : 'Create Event'}</button>
      </form>
    </div>
  );
}
